import { Term } from "./term";
import { HasSocketEvent, Validateable } from "@models/interfaces";
import { UserVerificationState } from "./user";
import { Time } from "@models/time";
import { SECONDS_IN_DAYS } from "@helpers/constants";
import { SocketEvent } from "@helpers/types";

export class Account extends Validateable implements HasSocketEvent {
	private fiat_id: string = "";
	private language: string = "";
	private marketing_tracking: boolean = false;
	private swap_disabled: boolean = false;
	private savings_disabled: boolean = false;
	private verified: string = "";
	private bfc_enabled: boolean = false;
	private is_best2_terms_accepted: boolean = false;
	private is_advanced_trading_accepted: boolean = false;
	private digital_securities_trading_appropriateness: string = "";
	private instant_trade_bonus_disabled: boolean = false;
	private review_period_starts_at: Time = new Time();
	private review_period_ends_at: Time = new Time();
	private terms: Term[] = [new Term()];
	public eventData: SocketEvent;
	private force_kyc_review: boolean = false;

	public getEvent(): string {
		return this.eventData;
	}

	public getFiatId(): string {
		return this.fiat_id;
	}

	public get bestFeeOptIn(): boolean {
		return this.bfc_enabled;
	}

	public set bestFeeOptIn(b: boolean) {
		this.bfc_enabled = b;
	}

	public getLanguage(): string {
		return this.language;
	}

	public getReviewStartsDays(): number {
		return this.review_period_starts_at.getUnix() / SECONDS_IN_DAYS;
	}

	public getReviewEndsDays(): number {
		return this.review_period_ends_at.getUnix() / SECONDS_IN_DAYS;
	}

	public getReviewPeriodStart(): Time {
		return this.review_period_starts_at;
	}

	public getReviewPeriodEnd(): Time {
		return this.review_period_ends_at;
	}

	public removeReviewDate(): void {
		this.review_period_starts_at = new Time();
		this.review_period_ends_at = new Time();
	}

	public isBest2TermsAccepted(): boolean {
		return this.is_best2_terms_accepted;
	}

	// BEST2 needs some fun magic with the terms while also doing a boolean...
	public acceptBest2Terms(): void {
		this.is_best2_terms_accepted = true;
		const best2Term: Term = new Term();
		best2Term.setType("best2_terms");
		best2Term.setSigned(true);
		this.terms.push(best2Term);
	}

	public isAdvancedTradingAccepted(): boolean {
		return this.is_advanced_trading_accepted;
	}

	public acceptAdvancedTrading(): void {
		this.is_advanced_trading_accepted = true;
		const advTradingTerm: Term = new Term();
		advTradingTerm.setType("advanced_trading");
		advTradingTerm.setSigned(true);
		this.terms.push(advTradingTerm);
	}

	public isDocumented(): boolean {
		return this.verified === UserVerificationState.DOCUMENTED;
	}

	public get _language(): string {
		return this.language;
	}

	public set _language(s: string) {
		this.language = s;
	}

	public get _fiat_id(): string {
		return this.fiat_id;
	}

	public set _fiat_id(s: string) {
		this.fiat_id = s;
	}

	public getGoogleTracking(): boolean {
		return this.marketing_tracking;
	}

	public setGoogleTracking(b: boolean): void {
		this.marketing_tracking = b;
	}

	public getSwapDisabled(): boolean {
		return this.swap_disabled;
	}

	public setSwapDisabled(b: boolean): void {
		this.swap_disabled = b;
	}

	public getSavingsDisabled(): boolean {
		return this.savings_disabled;
	}

	public setSavingsDisabled(b: boolean): void {
		this.savings_disabled = b;
	}

	public getTradingAppropriateness(): string {
		return this.digital_securities_trading_appropriateness;
	}

	// Check for `isNew` because of instantiation
	public getTerms(): Term[] {
		return this.terms.filter((t) => !t.isNew());
	}

	// BEST 2.0 opt-in terms
	public getTermsForUpdate(): Term[] {
		return this.terms.filter((t) => !t.isNew() || t.getType() === "best2_terms");
	}

	public updateOriginalTerms(): void {
		this.terms.forEach((term) => {
			term.originalSetSigned(term.hasSigned());
		});
	}

	public isInstantTradeBonusDisabled(): boolean {
		return this.instant_trade_bonus_disabled;
	}

	public shouldForceKycFlow(): boolean {
		return this.force_kyc_review;
	}
}
